import { useNavigate } from "@tanstack/react-router";
import {
	getCoreRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useTranslate } from "@tolgee/react";
import { useAtom } from "jotai";
import {
	useCallback,
	useDeferredValue,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { analytics } from "#/lib/analytics";
import { matchesSearchQuery } from "#/lib/hangul-search";
import { useTradePerpMarkets } from "../../hooks/use-trade-data.ts";
import { getSearchableMarketNames } from "../../lib/market-display.ts";
import { perpMarketColumns } from "./perp-market-columns.tsx";
import { favoriteMarketsAtom } from "./stores/favorite-markets.ts";

const coreRowModel = getCoreRowModel();
const sortedRowModel = getSortedRowModel();

const ROW_HEIGHT = 38;

export type MarketCategory = "all" | "perp" | "hip3" | "favorites";

export const CATEGORY_TABS: Array<{ key: MarketCategory; labelKey: string }> = [
	{ key: "all", labelKey: "market-search.tab.all" },
	{ key: "perp", labelKey: "market-search.tab.perp" },
	{ key: "hip3", labelKey: "market-search.tab.hip3" },
	{ key: "favorites", labelKey: "market-search.tab.favorites" },
];

export function usePerpMarketSearch() {
	const { t } = useTranslate();
	const navigate = useNavigate();
	const { markets, isLoading, isError } = useTradePerpMarkets();
	const [favorites, setFavorites] = useAtom(favoriteMarketsAtom);
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [sorting, setSorting] = useState<SortingState>([]);
	const [category, setCategory] = useState<MarketCategory>("all");
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const inputRef = useRef<HTMLInputElement>(null);
	const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
		null,
	);
	const deferredQuery = useDeferredValue(query);

	useEffect(() => {
		if (open) return;
		setQuery("");
		setCategory("all");
		setHighlightedIndex(-1);
	}, [open]);

	useEffect(() => {
		if (!open) return;
		inputRef.current?.focus();
	}, [open]);

	useEffect(() => {
		function handleGlobalKeyDown(e: KeyboardEvent) {
			if ((e.metaKey || e.ctrlKey) && e.key === "k") {
				e.preventDefault();
				setOpen(true);
				return;
			}
			if (e.key === "/" && !open) {
				const tag = (e.target as HTMLElement)?.tagName;
				if (
					tag === "INPUT" ||
					tag === "TEXTAREA" ||
					(e.target as HTMLElement)?.isContentEditable
				) {
					return;
				}
				e.preventDefault();
				setOpen(true);
			}
		}
		document.addEventListener("keydown", handleGlobalKeyDown);
		return () => document.removeEventListener("keydown", handleGlobalKeyDown);
	}, [open]);

	const favoritesSet = useMemo(() => new Set(favorites), [favorites]);

	const toggleFavorite = useCallback(
		(coin: string) => {
			setFavorites((prev) =>
				prev.includes(coin) ? prev.filter((c) => c !== coin) : [...prev, coin],
			);
		},
		[setFavorites],
	);

	const filteredMarkets = useMemo(() => {
		let filtered = markets;
		if (category === "perp") {
			filtered = markets.filter((m) => !m.dex);
		} else if (category === "hip3") {
			filtered = markets.filter((m) => m.dex === "xyz");
		} else if (category === "favorites") {
			filtered = markets.filter((m) => favoritesSet.has(m.coin));
		}

		const normalizedQuery = deferredQuery.trim().toLowerCase();
		if (normalizedQuery) {
			filtered = filtered.filter((market) => {
				const searchableValues = [
					market.coin,
					market.displaySymbol,
					market.displayName,
					...getSearchableMarketNames({
						coin: market.coin,
						dex: market.dex,
					}),
				];

				return matchesSearchQuery(normalizedQuery, searchableValues);
			});
		}

		return filtered;
	}, [markets, deferredQuery, favoritesSet, category]);

	const totalQueryMatchCount = useMemo(() => {
		const normalizedQuery = deferredQuery.trim().toLowerCase();
		if (!normalizedQuery) return 0;
		let count = 0;
		for (const market of markets) {
			const searchableValues = [
				market.coin,
				market.displaySymbol,
				market.displayName,
				...getSearchableMarketNames({
					coin: market.coin,
					dex: market.dex,
				}),
			];
			if (matchesSearchQuery(normalizedQuery, searchableValues)) count++;
		}
		return count;
	}, [markets, deferredQuery]);

	const table = useReactTable({
		data: filteredMarkets,
		columns: perpMarketColumns,
		state: { sorting },
		onSortingChange: setSorting,
		getCoreRowModel: coreRowModel,
		getSortedRowModel: sortedRowModel,
	});

	const tableRows = table.getRowModel().rows;
	const rows = useMemo(() => {
		if (category === "favorites" || favoritesSet.size === 0) return tableRows;
		const favs = [];
		const rest = [];
		for (const row of tableRows) {
			if (favoritesSet.has(row.original.coin)) favs.push(row);
			else rest.push(row);
		}
		return [...favs, ...rest];
	}, [tableRows, favoritesSet, category]);

	useEffect(() => {
		setHighlightedIndex(-1);
	}, [rows]);

	const virtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => scrollElement,
		estimateSize: () => ROW_HEIGHT,
		overscan: 10,
	});

	useEffect(() => {
		if (highlightedIndex >= 0 && rows.length > 0) {
			virtualizer.scrollToIndex(highlightedIndex, { align: "auto" });
		}
	}, [highlightedIndex, virtualizer, rows.length]);

	const rowsRef = useRef(rows);
	rowsRef.current = rows;

	const handleSelect = useCallback(
		(routeSymbol: string) => {
			setOpen(false);
			analytics.track("market_selected", {
				symbol: routeSymbol,
				source: category,
			});
			void navigate({
				to: "/trade/$symbol",
				params: { symbol: routeSymbol },
			});
		},
		[navigate, category],
	);

	const handleContentKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			const currentRows = rowsRef.current;

			if (e.key === "ArrowDown") {
				e.preventDefault();
				setHighlightedIndex((prev) =>
					prev < 0 ? 0 : Math.min(prev + 1, currentRows.length - 1),
				);
				return;
			}

			if (e.key === "ArrowUp") {
				e.preventDefault();
				setHighlightedIndex((prev) => (prev < 0 ? 0 : Math.max(prev - 1, 0)));
				return;
			}

			if (e.key === "Enter") {
				e.preventDefault();
				if (highlightedIndex >= 0 && highlightedIndex < currentRows.length) {
					handleSelect(currentRows[highlightedIndex].original.routeSymbol);
				}
				return;
			}

			if ((e.metaKey || e.ctrlKey) && e.key === "f") {
				e.preventDefault();
				if (highlightedIndex >= 0 && highlightedIndex < currentRows.length) {
					toggleFavorite(currentRows[highlightedIndex].original.coin);
				}
			}
		},
		[handleSelect, highlightedIndex, toggleFavorite],
	);

	const emptyMessage =
		category === "favorites" && !deferredQuery.trim()
			? t("market-search.no-favorites")
			: t("market-search.no-results");

	const showCrossCategoryHint =
		category !== "all" &&
		rows.length === 0 &&
		deferredQuery.trim() !== "" &&
		totalQueryMatchCount > 0;

	return {
		open,
		setOpen,
		query,
		setQuery,
		category,
		setCategory,
		highlightedIndex,
		inputRef,
		setScrollElement,
		favorites,
		favoritesSet,
		toggleFavorite,
		isLoading,
		isError,
		table,
		rows,
		virtualizer,
		handleSelect,
		handleContentKeyDown,
		emptyMessage,
		showCrossCategoryHint,
		totalQueryMatchCount,
	};
}
